import { Injectable, inject } from '@angular/core';

import { map, catchError } from 'rxjs/operators';
import { firstValueFrom, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { ApiService } from './api.service';
import { Profile } from '../models/profile.model';
import {
  AddressInformation,
  AddressType,
  CardContactInfo,
  ContactInfoFactory,
  ContactType,
  SocialMedia,
  User,
} from '../models';
import { ProfileForm } from '../forms';
import { UpdateProfileComplete } from '../state-mgmt/profile/profile.actions';
import { NotificationEventTypes, NotificationEvent } from '../notifications/notification-event';
import { NotificationEventService } from '../notifications/notification-event.service';
import { UserProfileUpdated } from '../state-mgmt';

/**
 * Client side service for interacting with the server to retrieve and update profiles.
 */
@Injectable()
export class ProfileService {
  private store = inject<Store<any>>(Store);
  private apiService = inject(ApiService);
  private notificationService = inject(NotificationEventService);

  resource = 'profiles';
  addDelegateEndpoint = 'add-delegate';
  removeDelegateEndpoint = 'remove-delegate';

  /**
   * Update an existing profile with
   *
   * @param id
   * @param profile
   * @return an Observable that results in a Profile
   */
  updateProfile(id: string, profile: Profile): Observable<Profile> {
    const clonedProfile = { ...profile };
    delete clonedProfile.audit;
    return this.apiService.put(`profiles/${id}`, clonedProfile).pipe(map((body) => new Profile(body)));
  }

  addDelegate(fromUser: string, toUser: string): Observable<User> {
    const payload = {
      fromUser,
      toUser,
    };
    return this.apiService.post(`${this.resource}/${this.addDelegateEndpoint}`, payload).pipe(map((body) => new User(body)));
  }

  removeDelegate(fromUser: string, toUser: string): Observable<User> {
    const payload = {
      fromUser,
      toUser,
    };
    return this.apiService.post(`${this.resource}/${this.removeDelegateEndpoint}`, payload).pipe(
      map((body) => new User(body)),
    );
  }

  updateProfilePartial(profile: Profile, fields?: string[]): Observable<Profile> {
    let payload: any;

    payload = profile;
    if (fields && fields.length) {
      payload = {};
      // order.orderState = order.listing.orderState;
      fields.forEach((field) => {
        payload[field] = profile[field];
      });
    }
    delete payload.audit;
    return this.apiService.put(`${this.resource}/${profile._id}`, payload).pipe(
      map((body) => new Profile(body)),
    );
  }

  async updateProfileAddress(
    profile: Profile,
    type: AddressType,
    value: AddressInformation,
  ): Promise<Profile> {
    const updatedProfile = new Profile(profile);
    const address = new AddressInformation(value);
    address.type = type;

    const contactInfo = updatedProfile.contactInfo.find(
      (contact) => contact.type === ContactType.card && contact.address.type === type,
    );

    if (contactInfo) {
      contactInfo.address = address;
    } else {
      updatedProfile.contactInfo.push(new CardContactInfo({
        type: ContactType.card,
        address,
      }));
    }

    const savedProfile = await firstValueFrom(this.updateProfilePartial(updatedProfile, ['contactInfo']));
    this.store.dispatch(UpdateProfileComplete({ payload: savedProfile }));
    this.store.dispatch(UserProfileUpdated({ payload: savedProfile }));
    return savedProfile;
  }

  async patch(profileForm: ProfileForm, isCurrentUser?: boolean, isVipProfileUpdate?: boolean): Promise<Profile> {
    // Gets all the dirty fields that have changed
    const dirty = profileForm.getDirty();
    dirty.contactInfo = this.getSocialMedia(profileForm.originalValue as Profile, profileForm.value);
    if (profileForm.value.phoneNumbers?.length && !profileForm.value.phoneNumbers[0].primary) {
      profileForm.value.phoneNumbers.forEach((phoneNum, ind) => {
        if (ind === 0) {
          phoneNum.primary = true;
        } else {
          phoneNum.primary = false;
        }
      });
      dirty.phoneNumbers = profileForm.value.phoneNumbers;
    }
    if (dirty.contactInfo?.length === 0) {
      dirty.contactInfo = [];
    }
    const updatedProfile = await this.apiService.put(`${this.resource}/${profileForm.id}`, dirty).pipe(
      map((body) => new Profile(body)),
      catchError((error) => {
        // Display toaster error
        console.error('There was an error patching the profile values', error, dirty);
        const event = new NotificationEvent(NotificationEventTypes.ERROR, 'An error occured updating your profile');
        this.notificationService.getEventEmitter().emit(event);
        return of(null);
      }),
    ).toPromise();

    if (updatedProfile) {
      this.store.dispatch(UpdateProfileComplete({ payload: updatedProfile }));
      if (isCurrentUser) {
        // If we are updating the current user, emit an event to update the profile
        // display information
        this.store.dispatch(UserProfileUpdated({ payload: updatedProfile }));
      }
    }
    return updatedProfile;
  }

  getSocialMedia(profile: Profile, socialMedia: SocialMedia) {
    const getSocialMediaType = ContactInfoFactory.getContactInfoSocialMediaFactory(socialMedia);
    const getProfileAddress = profile.contactInfo.filter((item) => item.type === 'card');
    return getProfileAddress?.length ? [...getProfileAddress, ...getSocialMediaType] : getSocialMediaType;
  }

  /**
   * Get a profile by id
   *
   * @param id the profile id
   */
  getProfile(id: string): Observable<Profile> {
    console.log('Get profile for id = %s', id);
    return this.apiService.get(`${this.resource}/${id}`).pipe(map((body) => new Profile(body)));
  }
}
